<template>

   <div class="offcanvas-header mx-4">
      <div>
         <p class="offcanvas-title pb-1">{{ lead.organisation_name }}</p>
         <div v-if="!lead.assigned_user_id || lead.assigned_user_id === 0" class="alert alert-warning animate__animated animate__headShake mb-3 d-flex align-items-center" style="background: rgba(251, 191, 36, 0.08); border: 1px solid rgba(251, 191, 36, 0.4); border-left: 4px solid #fbbf24; border-radius: 8px; padding: 12px 16px; gap: 12px; color: #b45309; font-size: 0.85rem; line-height: 1.4;">
            <i class="fa-solid fa-triangle-exclamation" style="font-size: 1.2rem;"></i>
            <div>
               <strong>Warning:</strong> This lead has no BDM assigned to it. You will not be able to send any proposals.
            </div>
         </div>
         <div v-else class="mb-3">
            <span class="badge bg-light text-dark border" style="font-weight: 500; font-size: 0.8rem;">
               <i class="fa-solid fa-user-tie me-1" style="color: #00A0C1;"></i>
               Assigned: {{ assignedUserName }}
            </span>
         </div>
      
         <div class="leads-menu-tab">
            <ul class="nav nav-pills" id="pills-tab" role="tablist">
               <li class="nav-item" role="presentation">
                  <button class="nav-link active" id="pills-details-tab" data-bs-toggle="pill" data-bs-target="#pills-details" 
                  type="button" role="tab" aria-controls="pills-details" aria-selected="true"
                  @click="onButtonClick('details')">Details</button>
               </li>
               <div class="vr"></div>
            
               <li v-if="hasPentest" class="nav-item" role="presentation">
                  <button class="nav-link" id="pills-pentest-tab" data-bs-toggle="pill" data-bs-target="#pills-pentest" 
                     type="button" role="tab" aria-controls="pills-pentest" aria-selected="false" 
                     @click="onButtonClick('pentest')">Penetration Test</button>
               </li>
               <div v-if="hasPentest" class="vr"></div>
               
               <li class="nav-item" role="presentation">
                  <button class="nav-link" id="pills-proposal-tab" data-bs-toggle="pill" data-bs-target="#pills-proposal" 
                     type="button" role="tab" aria-controls="pills-proposal" aria-selected="false"
                     @click="onButtonClick('proposal')">Proposals & Contracts</button>
               </li>
               <div class="vr"></div>

               <li class="nav-item" role="presentation">
                  <button class="nav-link" id="pills-activity-tab" data-bs-toggle="pill" data-bs-target="#pills-activity" 
                     type="button" role="tab" aria-controls="pills-activity" aria-selected="false"
                     @click="onButtonClick('activity')">Activity</button>
               </li>
               <div class="vr"></div>

               <li class="nav-item" role="presentation">
                  <button class="nav-link" id="pills-files-tab" data-bs-toggle="pill" data-bs-target="#pills-files" 
                     type="button" role="tab" aria-controls="pills-files" aria-selected="false"
                     @click="onButtonClick('files')">Files</button>
               </li>
            </ul>

            <hr class="menu-divider"/>
         </div>
      </div>
   </div>

   <div ref="sidebarBodyRef" class="offcanvas-body mx-4">
      <div class="tab-content" id="pills-tabContent">

         <!-- DETAILS -->
         <div class="tab-pane fade show active" id="pills-details" role="tabpanel" aria-labelledby="pills-details-tab">
            <LeadForm ref="leadFormRef" @onSubmit="onSubmitForm"/>
         </div>

         <!-- PENTEST -->
         <div v-if="hasPentest" class="tab-pane fade" id="pills-pentest" role="tabpanel" aria-labelledby="pills-pentest-tab">
            <MainPenTestForm ref="penTestFormRef" :items="pentestDetails"
               @downloadFile="downloadFile" @downloadAllFiles="downloadAllFiles"/>
         </div>

         <!-- ACTIVITY -->
         <div class="tab-pane fade" id="pills-activity" role="tabpanel" aria-labelledby="pills-activity-tab">
            <LeadActivity ref="leadActivityRef"/>
         </div>

         <!-- ===== PROPOSALS TAB ===== -->
         <div class="tab-pane fade" id="pills-proposal" role="tabpanel" aria-labelledby="pills-proposal-tab">
            <div v-if="isLoadingProposals" class="sidebar-loading">
               <i class="fa-solid fa-spinner fa-spin me-2"></i> Loading proposals...
            </div>
            <template v-else>
               <!-- Filters -->
               <div class="sidebar-filters">
                  <select v-model="proposalFilters.status" class="sidebar-filter-select">
                     <option value="">All Statuses</option>
                     <option value="0">Draft</option>
                     <option value="1">Sent</option>
                     <option value="2">Resent</option>
                     <option value="3">Accepted</option>
                     <option value="4">Declined</option>
                     <option value="5">Expired</option>
                     <option value="6">Completed</option>
                  </select>
                  <input type="date" v-model="proposalFilters.dateFrom" class="sidebar-filter-date" title="From date" />
                  <span class="sidebar-filter-sep">—</span>
                  <input type="date" v-model="proposalFilters.dateTo" class="sidebar-filter-date" title="To date" />
                  <button class="sidebar-filter-clear" @click="clearProposalFilters" title="Clear filters">
                     <i class="fa-solid fa-xmark"></i>
                  </button>
               </div>

               <!-- Results count -->
               <div class="sidebar-results-info" v-if="!isLoadingProposals">
                  {{ filteredProposals.length }} proposal{{ filteredProposals.length !== 1 ? 's' : '' }}
                  <span v-if="proposalFilters.status || proposalFilters.dateFrom || proposalFilters.dateTo"> (filtered)</span>
               </div>

               <!-- Empty -->
               <div v-if="filteredProposals.length === 0" class="sidebar-empty">
                  <i class="fa-regular fa-file-lines fa-2x mb-2" style="color:#ccc;"></i>
                  <p v-if="leadProposals.length === 0">No proposals have been created for this lead yet.</p>
                  <p v-else>No proposals match the selected filters.</p>
               </div>

               <!-- Cards -->
               <div v-else class="sidebar-card-list">
                   <div v-for="proposal in pagedProposals" :key="proposal.id" class="sidebar-card">
                      <div class="sidebar-card-header">
                         <div class="sidebar-card-title">{{ proposal.proposal_title || 'Untitled Proposal' }}</div>
                         <span class="spark-pill-badge" :class="getProposalStatusBadgeClass(proposal.proposal_status)">
                            <i :class="getProposalStatusIcon(proposal.proposal_status)"></i>
                            {{ getProposalStatusText(proposal.proposal_status) }}
                         </span>
                      </div>
                      <div class="sidebar-card-meta">
                         <span v-if="proposal.proposal_version" class="spark-pill-badge" :class="getVersionBadgeClass(proposal.proposal_version)"><i class="fa-solid fa-code-branch"></i> {{ proposal.proposal_version }}</span>
                         <span v-if="proposal.proposal_total_amount"><i class="fa-solid fa-dollar-sign me-1"></i>{{ formatCurrency(proposal.proposal_total_amount) }}</span>
                         <span><i class="fa-regular fa-calendar me-1"></i>{{ formatDate(proposal.created_at) }}</span>
                      </div>
                      <div class="sidebar-card-actions">
                         <button class="sidebar-action-btn primary" @click="openProposalBDMView(proposal)">
                            <i class="fa-solid fa-arrow-up-right-from-square me-1"></i> Open Proposal
                         </button>
                         <button class="sidebar-action-btn secondary" @click="openProposalClientPage(proposal)" :disabled="!proposal.unique_key || !proposal.external_key">
                            <i class="fa-solid fa-eye me-1"></i> Client Page
                         </button>
                      </div>

                      <!-- Linked Contracts (always visible) -->
                      <div v-if="getContractsForProposal(proposal.id).length > 0" class="sidebar-inline-contracts">
                         <div class="sidebar-inline-label">
                            <i class="fa-solid fa-file-contract me-1"></i>
                            Linked Contracts
                         </div>
                         <div v-for="contract in getContractsForProposal(proposal.id)" :key="contract.id"
                            class="sidebar-inline-contract">
                            <div class="sidebar-inline-contract-header">
                               <span class="sidebar-inline-contract-title">{{ contract.contract_title || 'Untitled Contract' }}</span>
                               <span class="spark-pill-badge" :class="getContractStatusBadgeClass(contract.contract_status)">
                                  <i :class="getContractStatusIcon(contract.contract_status)"></i>
                                  {{ getContractStatusText(contract.contract_status) }}
                               </span>
                            </div>
                            <div class="sidebar-inline-contract-meta">
                               <span v-if="contract.contract_version" class="spark-pill-badge" :class="getVersionBadgeClass(contract.contract_version)"><i class="fa-solid fa-code-branch"></i> {{ contract.contract_version }}</span>
                               <span><i class="fa-regular fa-calendar me-1"></i>{{ formatDate(contract.created_at) }}</span>
                             </div>
                            <div class="sidebar-inline-contract-actions">
                              <button class="sidebar-action-btn primary" style="font-size:11px; padding:3px 8px;" @click="openContractBDMView(contract)">
                                 <i class="fa-solid fa-arrow-up-right-from-square me-1"></i> Open
                              </button>
                              <button class="sidebar-action-btn secondary" style="font-size:11px; padding:3px 8px;"
                                 @click="openContractClientPage(contract)" :disabled="!contract.unique_key || !contract.external_key">
                                 <i class="fa-solid fa-eye me-1"></i> Client Page
                              </button>
                           </div>
                        </div>
                     </div>
                  </div>
               </div>

               <!-- Pagination -->
               <div v-if="filteredProposals.length > proposalPageSize" class="sidebar-pagination">
                  <button class="sidebar-page-btn" :disabled="proposalPage === 1" @click="proposalPage--">
                     <i class="fa-solid fa-chevron-left"></i>
                  </button>
                  <span class="sidebar-page-label">{{ proposalPage }} / {{ proposalTotalPages }}</span>
                  <button class="sidebar-page-btn" :disabled="proposalPage === proposalTotalPages" @click="proposalPage++">
                     <i class="fa-solid fa-chevron-right"></i>
                  </button>
               </div>
            </template>
         </div>
         <!-- ===== END PROPOSALS TAB ===== -->

         <!-- ===== END CONTRACTS TAB (removed — now nested under Proposals) ===== -->

         <!-- FILES -->
         <div class="tab-pane fade" id="pills-files" role="tabpanel" aria-labelledby="pills-files-tab">
            <LeadFileList ref="leadFileListRef" @downloadFile="downloadFile" @downloadAllFiles="downloadAllFiles"/>
         </div>
         
      </div>
   </div>
   
</template>

<script>
import LeadForm from '@/components/Leads/LeadForm.vue';
import MainPenTestForm from '@/components/Leads/Pentest/MainPenTestForm.vue';
import LeadActivity from '@/components/Leads/LeadActivity.vue';
import LeadFileList from '@/components/Leads/LeadFileList.vue';
import moment from 'moment';
import { Offcanvas } from 'bootstrap';

const PAGE_SIZE = 5;

export default ({
   components: { LeadForm, MainPenTestForm, LeadActivity, LeadFileList },

   props: {
      id: { type: String },
   },

   emits: ['onSubmit'],

   data() {
      return {
         lead: {},
         expandedProposals: {},
         hasPentest: false,
         pentestDetails: {},

         // --- Proposals ---
         leadProposals: [],
         isLoadingProposals: false,
         proposalFilters: { status: '', dateFrom: '', dateTo: '' },
         proposalPage: 1,
         proposalPageSize: PAGE_SIZE,

         // --- Contracts ---
         leadContracts: [],
         isLoadingContracts: false,
         contractFilters: { status: '', dateFrom: '', dateTo: '' },
         contractPage: 1,
         contractPageSize: PAGE_SIZE,
      }
   },

   created() { this.init() },

   computed: {
      item() { return this.id },

      assignedUserName() {
         if (this.lead.assigned_user) {
            return `${this.lead.assigned_user.first_name} ${this.lead.assigned_user.last_name}`
         }
         return '-no assigned vertex bdm-'
      },

      // ---- Proposal computed ----
      filteredProposals() {
         return this.leadProposals.filter(p => {
            if (this.proposalFilters.status !== '' && String(p.proposal_status) !== this.proposalFilters.status) return false
            if (this.proposalFilters.dateFrom && moment(p.created_at).isBefore(this.proposalFilters.dateFrom, 'day')) return false
            if (this.proposalFilters.dateTo   && moment(p.created_at).isAfter(this.proposalFilters.dateTo,   'day')) return false
            return true
         })
      },
      proposalTotalPages() {
         return Math.max(1, Math.ceil(this.filteredProposals.length / this.proposalPageSize))
      },
      pagedProposals() {
         const start = (this.proposalPage - 1) * this.proposalPageSize
         return this.filteredProposals.slice(start, start + this.proposalPageSize)
      },

      // ---- Contract computed ----
      filteredContracts() {
         return this.leadContracts.filter(c => {
            if (this.contractFilters.status !== '' && String(c.contract_status) !== this.contractFilters.status) return false
            if (this.contractFilters.dateFrom && moment(c.created_at).isBefore(this.contractFilters.dateFrom, 'day')) return false
            if (this.contractFilters.dateTo   && moment(c.created_at).isAfter(this.contractFilters.dateTo,   'day')) return false
            return true
         })
      },
      contractTotalPages() {
         return Math.max(1, Math.ceil(this.filteredContracts.length / this.contractPageSize))
      },
      pagedContracts() {
         const start = (this.contractPage - 1) * this.contractPageSize
         return this.filteredContracts.slice(start, start + this.contractPageSize)
      },
   },

   watch: {
      item() { this.init() },
      // Reset page when filters change
      proposalFilters: { deep: true, handler() { this.proposalPage = 1 } },
      contractFilters: { deep: true, handler() { this.contractPage = 1 } },
   },

   methods: {
      init() {
         this.hasPentest = false
         this.leadProposals = []
         this.leadContracts = []
         this.proposalPage = 1
         this.contractPage = 1
         this.getLead(this.id)
      },

      onSubmitForm(updatedData) {
         const currentUserId = this.$store.state.auth.currentUser?.id || this.$store.state.auth.jwtPayload?.user_id;
         const isConsultant = this.$store.state.auth.isConsultant;
         const assignedUserId = updatedData?.assigned_user_id || this.$refs.leadFormRef?.lead?.assigned_user_id || this.lead.assigned_user_id;
         const assignedToOther = assignedUserId && assignedUserId !== currentUserId;

         if (isConsultant && assignedToOther) {
            this.closeSidebar();
            this.$emit('onSubmit');
            return;
         }
         this.init();
         this.$emit('onSubmit');
      },

      closeSidebar() {
         const sidebarEl = document.getElementById('leadSidebar');
         if (sidebarEl) {
            const offcanvasInstance = Offcanvas.getInstance(sidebarEl) || Offcanvas.getOrCreateInstance(sidebarEl);
            if (offcanvasInstance) {
               offcanvasInstance.hide();
            }
         }
      },

      onButtonClick(tab) {
         this.$refs.sidebarBodyRef.scrollTo(0, 0)
         if (tab === 'details')   this.$refs.leadFormRef.isEdit(this.id)
         else if (tab === 'pentest')   this.$refs.penTestFormRef.resizeTextareas()
         else if (tab === 'proposal')  { this.getLeadProposals(); this.getLeadContracts(); }
         else if (tab === 'contracts') this.getLeadContracts()
         else if (tab === 'activity')  this.$refs.leadActivityRef.init(this.id)
         else if (tab === 'files')     this.$refs.leadFileListRef.init(this.id)
      },

      async getLead(id) {
         await this.$axios.get('/leads/' + id)
            .then(response => {
               this.lead = response.data.lead
               if (this.lead.penetration_test) {
                  this.hasPentest = true
                  this.pentestDetails = this.lead.penetration_test
               }
               if (this.$refs.leadFormRef) {
                  this.$refs.leadFormRef.isEdit(response.data.lead.id)
               }
            })
            .catch(error => {
               if (error.response && (error.response.status === 403 || error.response.status === 401 || error.response.status === 404)) {
                  this.closeSidebar();
                  this.$emit('onSubmit');
               } else {
                  this.$toast.error(error.response?.data?.message || error.message || 'Error loading lead');
               }
            })
      },

      async getLeadProposals() {
         if (!this.id) return
         this.isLoadingProposals = true
         try {
            const res = await this.$axios.get(`/leads/${this.id}/vertex-proposals`)
            this.leadProposals = res.data.vertex_proposals || []
         } catch (e) {
            this.$toast.error(e.response?.data?.message || 'Failed to load proposals')
         } finally {
            this.isLoadingProposals = false
         }
      },

      async getLeadContracts() {
         if (!this.id) return
         this.isLoadingContracts = true
         try {
            const res = await this.$axios.get(`/leads/${this.id}/contracts`)
            this.leadContracts = res.data.vertex_contracts || []
         } catch (e) {
            this.$toast.error(e.response?.data?.message || 'Failed to load contracts')
         } finally {
            this.isLoadingContracts = false
         }
      },

      clearProposalFilters() {
         this.proposalFilters = { status: '', dateFrom: '', dateTo: '' }
      },
      clearContractFilters() {
         this.contractFilters = { status: '', dateFrom: '', dateTo: '' }
      },

      // ---- Contract-Proposal grouping helpers ----
      getContractsForProposal(proposalId) {
         return this.leadContracts.filter(c => c.proposal_id === proposalId)
      },
      toggleProposalContracts(proposalId) {
         this.expandedProposals = { ...this.expandedProposals, [proposalId]: !this.expandedProposals[proposalId] }
      },

      // ---- Navigation helpers ----
      openProposalBDMView(proposal) {
         window.open(this.$router.resolve({ name: 'proposal-view', params: { id: proposal.id } }).href, '_blank')
      },
      openProposalClientPage(proposal) {
         if (!proposal.unique_key || !proposal.external_key) return
         window.open(this.$router.resolve({
            name: 'clientProposal',
            params: { unique_key: proposal.unique_key, access_key: proposal.external_key }
         }).href, '_blank')
      },
      openContractBDMView(contract) {
         window.open(this.$router.resolve({ name: 'contract-view', params: { id: contract.id } }).href, '_blank')
      },
      openContractClientPage(contract) {
         if (!contract.unique_key || !contract.external_key) return
         window.open(this.$router.resolve({
            name: 'clientContract',
            params: { unique_key: contract.unique_key, access_key: contract.external_key }
         }).href, '_blank')
      },
      openProposalFromContract(contract) {
         if (!contract.proposal_id) return
         window.open(this.$router.resolve({ name: 'proposal-view', params: { id: contract.proposal_id } }).href, '_blank')
      },

      resetForm() {
         this.hasPentest = false
         this.leadProposals = []
         this.leadContracts = []
      },

      // ---- Formatters ----
      formatDate(date) { return moment(date).format('DD-MMM-YYYY') },
      formatCurrency(value) {
         const num = parseFloat(value)
         if (isNaN(num)) return '-'
         return '$' + num.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      },

      // ---- Status helpers ----
      getProposalStatusText(s) {
         return ({ 0: 'Draft', 1: 'Sent', 2: 'Resent', 3: 'Accepted', 4: 'Declined', 5: 'Expired', 6: 'Completed' })[s] ?? 'Unknown'
      },
      getProposalStatusColor(s) {
         return ({ 0: '#6C757D', 1: '#00A0C1', 2: '#5B9BD5', 3: '#00A54F', 4: '#EB1C24', 5: '#F3994D', 6: '#6C757D' })[s] ?? '#6C757D'
      },
      getProposalStatusBadgeClass(s) {
         switch (Number(s)) {
            case 1: return 'spark-pill-badge--sent';
            case 2: return 'spark-pill-badge--resent';
            case 3: return 'spark-pill-badge--accepted';
            case 4: return 'spark-pill-badge--declined';
            case 5: return 'spark-pill-badge--expired';
            case 6: return 'spark-pill-badge--completed';
            default: return 'spark-pill-badge--draft';
         }
      },
      getProposalStatusIcon(s) {
         switch (Number(s)) {
            case 1: return 'fa-solid fa-paper-plane';
            case 2: return 'fa-solid fa-rotate-right';
            case 3: return 'fa-solid fa-circle-check';
            case 4: return 'fa-solid fa-circle-xmark';
            case 5: return 'fa-solid fa-calendar-xmark';
            case 6: return 'fa-solid fa-check-double';
            default: return 'fa-solid fa-pen';
         }
      },
      getContractStatusText(s) {
         return ({ 0: 'Draft', 1: 'Sent', 2: 'Viewed', 3: 'In Review', 4: 'Executed', 5: 'Cancelled', 6: 'Pending Approval', 7: 'Approved', 8: 'Declined' })[s] ?? 'Unknown'
      },
      getContractStatusColor(s) {
         return ({ 0: '#6C757D', 1: '#00A0C1', 2: '#8E6BD8', 3: '#F3994D', 4: '#00A54F', 5: '#EB1C24', 6: '#9C27B0', 7: '#00A54F', 8: '#EB1C24' })[s] ?? '#6C757D'
      },
      getContractStatusBadgeClass(s) {
         switch (Number(s)) {
            case 1: return 'spark-pill-badge--sent';
            case 2: return 'spark-pill-badge--viewed';
            case 3: return 'spark-pill-badge--in-review';
            case 4: return 'spark-pill-badge--executed';
            case 5: return 'spark-pill-badge--cancelled';
            case 6: return 'spark-pill-badge--pending-approval';
            case 7: return 'spark-pill-badge--approved';
            case 8: return 'spark-pill-badge--declined';
            default: return 'spark-pill-badge--draft';
         }
      },
      getContractStatusIcon(s) {
         switch (Number(s)) {
            case 1: return 'fa-solid fa-paper-plane';
            case 2: return 'fa-solid fa-eye';
            case 3: return 'fa-solid fa-hourglass-half';
            case 4: return 'fa-solid fa-file-signature';
            case 5: return 'fa-solid fa-ban';
            case 6: return 'fa-solid fa-clock-rotate-left';
            case 7: return 'fa-solid fa-check';
            case 8: return 'fa-solid fa-xmark';
            default: return 'fa-solid fa-pen';
         }
      },
      getVersionBadgeClass(versionNumber) {
         if (typeof versionNumber !== 'string' || !versionNumber.startsWith('v')) {
            return 'spark-pill-badge--version-default';
         }
         const parts = versionNumber.slice(1).split('.');
         if (parts.length !== 2) return 'spark-pill-badge--version-default';
         const minor = parseInt(parts[1], 10);
         if (isNaN(minor)) return 'spark-pill-badge--version-default';
         return minor > 0 ? 'spark-pill-badge--version-minor' : 'spark-pill-badge--version-major';
      },

      // ---- File download (unchanged) ----
      async downloadFile({ fileOrigin, file, testType }) {
         let apiPath = '/leads/' + this.lead.id + '/files/' + file.id
         if (fileOrigin === 'proposal')
            apiPath = '/leads/' + this.lead.id + '/proposals/' + file.lead_proposal_id + '/files/' + file.id

         await this.$axios.get(apiPath, { responseType: 'blob' })
            .then(response => {
               const url = window.URL.createObjectURL(new Blob([response.data]))
               const link = document.createElement('a')
               let fileName = this.lead.organisation_name + '_' + file.file_name
               if (testType) fileName = this.lead.organisation_name + '_' + testType + '_' + file.file_name
               link.href = url
               link.setAttribute('download', fileName)
               link.click()
            })
            .catch(error => { this.$toast.error(error.response.data.message) })

         if (testType) this.$refs.penTestFormRef.downloadFileDone(testType, file.id)
         else          this.$refs.leadFileListRef.downloadFileDone(file.id)
      },

      async downloadAllFiles({ testType, testTypeId }) {
         await this.$axios.get('/leads/' + this.lead.id + '/products/' + testTypeId + '/files', { responseType: 'blob' })
            .then(response => {
               const url = window.URL.createObjectURL(new Blob([response.data]))
               const link = document.createElement('a')
               link.href = url
               link.setAttribute('download', this.lead.organisation_name + '_' + testType + '_files.zip')
               link.click()
            })
            .catch(error => { this.$toast.error(error.response.data.message) })
         this.$refs.penTestFormRef.downloadAllFilesDone(this.lead.id, testType)
      },
   }
})
</script>

<style scoped>

.offcanvas-title {
   color: #435666;
   font-weight: 900;
   font-size: 24px;
}

.leads-menu-tab button { border-radius: 10px !important; }

.vr {
   width: 2px;
   height: 40px;
   margin-left: 1rem;
   margin-right: 1rem;
}

.menu-divider {
   width: 640px;
   border-width: 2px;
   margin-bottom: 1px;
}

.leads-menu-tab .nav-link {
   color: #435666;
   height: 40px;
   min-width: 95px;
   padding: 1rem;
   font-weight: 400;
}

.leads-menu-tab .nav-link.active {
   background-color: #00A0C1;
   color: #fff;
   font-weight: 900;
}

/* ===== Filters ===== */
.sidebar-filters {
   display: flex;
   align-items: center;
   gap: 6px;
   flex-wrap: wrap;
   margin-bottom: 10px;
   padding: 10px 12px;
   background: #f8fafc;
   border: 1px solid #e8ecef;
   border-radius: 10px;
}

.sidebar-filter-select {
   flex: 0 0 auto;
   height: 32px;
   font-size: 12px;
   padding: 0 8px;
   border: 1px solid #dde3ea;
   border-radius: 6px;
   background: #fff;
   color: #435666;
   outline: none;
   cursor: pointer;
}

.sidebar-filter-select:focus { border-color: #00A0C1; }

.sidebar-filter-date {
   flex: 1 1 120px;
   height: 32px;
   font-size: 12px;
   padding: 0 8px;
   border: 1px solid #dde3ea;
   border-radius: 6px;
   background: #fff;
   color: #435666;
   outline: none;
   min-width: 110px;
}

.sidebar-filter-date:focus { border-color: #00A0C1; }

.sidebar-filter-sep {
   font-size: 12px;
   color: #a0aec0;
   flex-shrink: 0;
}

.sidebar-filter-clear {
   height: 32px;
   width: 32px;
   display: flex;
   align-items: center;
   justify-content: center;
   border: 1px solid #dde3ea;
   border-radius: 6px;
   background: #fff;
   color: #718096;
   cursor: pointer;
   font-size: 13px;
   flex-shrink: 0;
   transition: background 0.15s, color 0.15s;
}
.sidebar-filter-clear:hover { background: #ffe8e8; color: #EB1C24; border-color: #EB1C24; }

/* ===== Results info ===== */
.sidebar-results-info {
   font-size: 12px;
   color: #718096;
   margin-bottom: 8px;
}

/* ===== Card list ===== */
.sidebar-card-list {
   display: flex;
   flex-direction: column;
   gap: 10px;
}

.sidebar-card {
   border: 1px solid #e8ecef;
   border-radius: 10px;
   padding: 14px 16px;
   background: #fff;
   box-shadow: 0 1px 4px rgba(0,0,0,0.05);
   transition: box-shadow 0.2s;
}
.sidebar-card:hover { box-shadow: 0 3px 10px rgba(0,160,193,0.12); }

.sidebar-card-header {
   display: flex;
   align-items: center;
   justify-content: space-between;
   gap: 10px;
   margin-bottom: 8px;
}

.sidebar-card-title {
   font-weight: 700;
   font-size: 14px;
   color: #2d3748;
   flex: 1;
   white-space: nowrap;
   overflow: hidden;
   text-overflow: ellipsis;
}

.sidebar-status-badge {
   font-size: 11px;
   font-weight: 700;
   color: #fff;
   padding: 3px 9px;
   border-radius: 20px;
   white-space: nowrap;
   letter-spacing: 0.3px;
   flex-shrink: 0;
}

.sidebar-card-meta {
   display: flex;
   flex-wrap: wrap;
   gap: 10px;
   font-size: 12px;
   color: #718096;
   margin-bottom: 10px;
}
.sidebar-card-meta span { display: flex; align-items: center; }

.sidebar-card-actions { display: flex; gap: 8px; flex-wrap: wrap; }

.sidebar-action-btn {
   display: inline-flex;
   align-items: center;
   font-size: 12px;
   font-weight: 600;
   padding: 5px 12px;
   border-radius: 6px;
   border: none;
   cursor: pointer;
   transition: opacity 0.15s, transform 0.1s;
}
.sidebar-action-btn:hover:not(:disabled) { opacity: 0.85; transform: translateY(-1px); }
.sidebar-action-btn:disabled { opacity: 0.45; cursor: not-allowed; }
.sidebar-action-btn.primary   { background: #00A0C1; color: #fff; }
.sidebar-action-btn.secondary { background: #f0f4f8; color: #435666; border: 1px solid #dde3ea; }

/* ===== Pagination ===== */
.sidebar-pagination {
   display: flex;
   align-items: center;
   justify-content: center;
   gap: 12px;
   margin-top: 14px;
   padding-top: 12px;
   border-top: 1px solid #e8ecef;
}

.sidebar-page-btn {
   width: 32px;
   height: 32px;
   display: flex;
   align-items: center;
   justify-content: center;
   border: 1px solid #dde3ea;
   border-radius: 6px;
   background: #fff;
   color: #435666;
   cursor: pointer;
   font-size: 13px;
   transition: background 0.15s, color 0.15s, border-color 0.15s;
}
.sidebar-page-btn:hover:not(:disabled) { background: #00A0C1; color: #fff; border-color: #00A0C1; }
.sidebar-page-btn:disabled { opacity: 0.4; cursor: not-allowed; }

.sidebar-page-label {
   font-size: 13px;
   font-weight: 600;
   color: #435666;
   min-width: 52px;
   text-align: center;
}

/* ===== Empty & Loading ===== */
.sidebar-empty {
   display: flex;
   flex-direction: column;
   align-items: center;
   padding: 36px 20px;
   text-align: center;
   color: #a0aec0;
   font-size: 14px;
}

.sidebar-loading {
   padding: 30px 0;
   text-align: center;
   color: #718096;
   font-size: 14px;
}

/* ===== Scrollbar ===== */
::-webkit-scrollbar { width: 8px; }
::-webkit-scrollbar-track {
   margin: 10px; border-radius: 10px;
   border-left: 2.5px solid transparent; border-right: 2.5px solid transparent;
   background-clip: content-box; -webkit-box-shadow: inset 0 0 5px #D9D9D9;
   background-color: #D9D9D9;
}
::-webkit-scrollbar-thumb { background: #00A0C1; border-radius: 10px; }
::-webkit-scrollbar-thumb:hover { background: #00A0C1; }

/* ===== Nested Contracts (Parent-Child) ===== */
/* ===== Inline Contracts (tree-view) ===== */
.sidebar-inline-contracts {
   margin-top: 10px;
   border-top: 1px dashed #e2e8f0;
   padding-top: 8px;
}

.sidebar-inline-label {
   font-size: 11px;
   font-weight: 600;
   color: #718096;
   text-transform: uppercase;
   letter-spacing: 0.04em;
   margin-bottom: 6px;
}

.sidebar-inline-contract {
   border-left: 3px solid #00A0C1;
   background-color: #f8fafb;
   border-radius: 0 6px 6px 0;
   padding: 8px 10px;
   margin-bottom: 6px;
   cursor: pointer;
   transition: background-color 0.15s ease, box-shadow 0.15s ease;
}
.sidebar-inline-contract:last-child {
   margin-bottom: 0;
}
.sidebar-inline-contract:hover {
   background-color: #edf7f9;
   box-shadow: 0 1px 4px rgba(0, 160, 193, 0.12);
}

.sidebar-inline-contract-header {
   display: flex;
   justify-content: space-between;
   align-items: center;
   gap: 6px;
}

.sidebar-inline-contract-title {
   font-size: 12px;
   font-weight: 600;
   color: #333;
   white-space: nowrap;
   overflow: hidden;
   text-overflow: ellipsis;
}

.sidebar-inline-contract-meta {
   display: flex;
   gap: 10px;
   font-size: 10.5px;
   color: #8a9bb0;
   margin-top: 3px;
}

.sidebar-inline-contract-actions {
   display: flex;
   gap: 6px;
   margin-top: 6px;
}

</style>